import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, capitalize, Checkbox, FormControlLabel, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CurrencyFormatField from 'components/common/CurrencyFormatField';
import { isFProject } from 'config/project';
import { CarModel, ExportMethod, ExportType, FuelType, PriceTerms, VehicleType } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import { exportCertificateRequired } from 'modules/cars/utils/common';
import auth from 'recoil/atoms/auth';


const exportTypes = Object.values(ExportType);
const exportMethods = Object.values(ExportMethod);
const vehicleTypes = Object.values(VehicleType);
const fuelTypes = Object.entries(FuelType);

type CarFormProps = {
    car: CarModel,
    onChange: (k: string, v: string | boolean | DateTime | null) => void,
    docPreview?: string | null,
}

export const CarFormFields = ({car, onChange, docPreview}: CarFormProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');

    return (
        <Grid container columnSpacing={2} alignItems="flex-end">
            <Grid item xs={6} sm={3}>
                <TextField
                    id="make"
                    label={t('make')}
                    fullWidth
                    required
                    value={car.make || ''}
                    onChange={e => onChange('make', e.target.value)}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="model"
                    label={t('model')}
                    fullWidth
                    required
                    value={car.model || ''}
                    onChange={e => onChange('model', e.target.value)}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="unitPrice"
                    label={t('price')}
                    fullWidth
                    value={car.unitPrice || ''}
                    onChange={e => onChange('unitPrice', e.target.value)}
                    error={!car.unitPrice}
                    InputProps={{
                        inputComponent: CurrencyFormatField as any,
                        startAdornment: <InputAdornment position='start'>¥</InputAdornment>,
                    }}
                />
            </Grid>

            {my.type !== CompanyType.PortAgent && (
                <>
                    <Grid item xs={6} sm={3}>
                        <TextField
                            id="priceTerms"
                            label={t('freight_payment')}
                            fullWidth
                            select
                            value={car.priceTerms || ''}
                            onChange={e => onChange('priceTerms', e.target.value)}
                            error={!car.priceTerms}
                        >
                            <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                            <MenuItem value={PriceTerms.CAF}>{t('freight_caf')}</MenuItem>
                            <MenuItem value={PriceTerms.FOB}>{t('freight_fob')}</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={4} sm={3}>
                        <TextField
                            id="exportMethod"
                            label={t('export_method')}
                            fullWidth
                            select
                            value={car.exportMethod || ''}
                            onChange={e => onChange('exportMethod', e.target.value)}
                            error={!car.exportMethod}
                        >
                            <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                            {exportMethods.map(i => <MenuItem key={i} value={i}>{capitalize(i)}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={4} sm={3}>
                        <TextField
                            id="exportType"
                            label={t('export_type')}
                            fullWidth
                            select
                            value={car.exportType || ''}
                            onChange={e => onChange('exportType', e.target.value)}
                            error={!car.exportType}
                        >
                            <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                            {exportTypes.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                        </TextField>
                    </Grid>
                </>
            )}

            <Grid item xs={4} sm={3}>
                <TextField
                    id="vehicleType"
                    label={t('vehicle_type')}
                    fullWidth
                    select
                    value={car.vehicleType || ''}
                    onChange={e => onChange('vehicleType', e.target.value)}
                    error={!car.vehicleType}
                >
                    <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                    {vehicleTypes.map(i => <MenuItem key={i} value={i}>{capitalize(i)}</MenuItem>)}
                </TextField>
            </Grid>

            <Grid item xs={4} sm={3}>
                {!isFProject && (
                    <FormControlLabel
                        label={t('damaged')}
                        control={<Checkbox
                            name="damaged"
                            checked={!!car.damaged}
                            onChange={e => onChange('damaged', e.target.checked)}
                            color="primary"
                        />}
                    />
                )}
            </Grid>

            <Box sx={{width: '100%'}}/>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="year"
                    label={t('year')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.year || ''}
                    onChange={e => onChange('year', e.target.value)}
                    error={exportCertificateRequired(car) && !car.year}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="color"
                    label={t('color')}
                    fullWidth
                    value={car.color || ''}
                    onChange={e => onChange('color', e.target.value)}
                />
            </Grid>

            <Box sx={{width: '100%'}}/>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="fuel"
                    label={t('fuel')}
                    fullWidth
                    select
                    value={car.fuel || ''}
                    onChange={e => onChange('fuel', e.target.value)}
                    error={!car.fuel}
                >
                    <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                    {fuelTypes.map(([name, value]) => <MenuItem key={value}
                                                                value={value}>{value} - {name.split(/(?=[A-Z])/).join(' ')}</MenuItem>)}
                </TextField>
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="engineSize"
                    label={`${t('engine_size')}, ${car.fuel === FuelType.ElectricVehicle ? 'kW' : 'cc'}`}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.engineSize || ''}
                    onChange={e => onChange('engineSize', e.target.value)}
                    error={!car.engineSize}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="engineModel"
                    label={t('engine_model')}
                    fullWidth
                    value={car.engineModel || ''}
                    onChange={e => onChange('engineModel', e.target.value)}
                    error={!car.engineModel}
                />
            </Grid>

            {car.exportType !== ExportType.Car &&
            <Grid item xs={6} sm={3}>
                <TextField
                    id="engineNumber"
                    label={t('engine_number')}
                    fullWidth
                    value={car.engineNumber || ''}
                    onChange={e => onChange('engineNumber', e.target.value)}
                />
            </Grid>
            }

            {car.fuel === FuelType.Diesel &&
            <Grid item xs={6} sm={3}>
                <TextField
                    id="enginePower"
                    label={t('engine_power')}
                    fullWidth
                    value={car.enginePower || ''}
                    onChange={e => onChange('enginePower', e.target.value)}
                />
            </Grid>
            }

            <Box sx={{width: '100%'}}/>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="seats"
                    label={t('seats')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.seats || ''}
                    onChange={e => onChange('seats', e.target.value)}
                    error={!car.seats}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="cargoCapacity"
                    label={t('cargo_capacity')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.cargoCapacity || ''}
                    onChange={e => onChange('cargoCapacity', e.target.value)}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="netWeight"
                    label={t('net_weight')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.netWeight || ''}
                    onChange={e => onChange('netWeight', e.target.value)}
                    error={!car.netWeight}
                />
            </Grid>

            <Grid item xs={6} sm={3}>
                <TextField
                    id="grossWeight"
                    label={t('gross_weight')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.grossWeight || ''}
                    onChange={e => onChange('grossWeight', e.target.value)}
                />
            </Grid>

            <Grid item xs={4} sm={3}>
                <TextField
                    id="length"
                    label={t('length')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.length || ''}
                    onChange={e => onChange('length', e.target.value)}
                    error={!car.length}
                />
            </Grid>

            <Grid item xs={4} sm={3}>
                <TextField
                    id="width"
                    label={t('width')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.width || ''}
                    onChange={e => onChange('width', e.target.value)}
                    error={!car.width}
                />
            </Grid>

            <Grid item xs={4} sm={3}>
                <TextField
                    id="height"
                    label={t('height')}
                    fullWidth
                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                    value={car.height || ''}
                    onChange={e => onChange('height', e.target.value)}
                    error={!car.height}
                />
            </Grid>

            <Grid item xs={12} sm={3}>
                {!!car.length && !!car.width && !!car.height && (
                    <>
                        {t('volume')}: {(car.length * car.width * car.height / 1000000).toFixed(3)}M<sup>3</sup>
                    </>
                )}
            </Grid>

            {(docPreview === 'export_certificate' || car.yard?.portId === 1) && (
                <>
                    <Box sx={{width: '100%'}}/>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            id="referenceNum"
                            label={t('reference_number')}
                            fullWidth
                            value={car.referenceNum || ''}
                            onChange={e => onChange('referenceNum', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <DatePicker
                            label={t('validity_period')}
                            format="yyyy-MM-dd"
                            slotProps={{textField: {fullWidth: true}}}
                            onChange={d => onChange('validUntil', d)}
                            value={car.validUntil}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
};
